import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';

const PAYING_KINDS = new Set(['bank_account', 'cash', 'mobile_money', 'hubtel_account']);

interface AccountItem {
  id: string;
  name: string;
  currency: string;
  kind: string;
}

export const ExpenseAccountField = ({
  open,
  value,
  onChange,
  currency,
}: {
  open: boolean;
  value: string | null;
  onChange: (accountId: string | null, currency: string | null) => void;
  currency?: string;
}) => {
  const { t } = useTranslation('spending');

  const accounts = useQuery({
    queryKey: ['financial-position', 'items', 'expense-account'],
    queryFn: () => api.get<{ data: AccountItem[] }>('/financial-position/items'),
    enabled: open,
  });

  const options = (accounts.data?.data ?? [])
    .filter((account) => PAYING_KINDS.has(account.kind))
    .filter((account) => !currency || account.currency === currency)
    .map((account) => ({
      value: account.id,
      label: account.name,
      currency: account.currency,
    }));

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{t('expenses.account')}</Label>
      <Listbox
        value={value}
        onChange={(next) => {
          const selected = options.find((option) => option.value === next) ?? null;
          onChange(next, selected?.currency ?? null);
        }}
        options={options.map(({ value: id, label }) => ({ value: id, label }))}
        placeholder={
          options.length === 0 ? t('expenses.accountEmpty') : t('expenses.accountPlaceholder')
        }
        popoverPortal="dialog"
        disabled={accounts.isLoading || options.length === 0}
      />
    </div>
  );
};
